import { KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { doc, updateDoc } from 'firebase/firestore';
import { useEffect, useRef, useState } from 'react';
import { firestore } from '../../services/firebase';
import type { User } from '../../types/auth';
import type { ProposalHeading } from '../../types/proposal';
import type { Tender } from '../../types/tender';

export const useStructureState = (
  tender: Tender | null,
  account: (User & { clientId?: string; aiSettings?: unknown }) | null,
  showSnackbar: (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info',
    duration?: number
  ) => void
) => {
  const [proposalStructure, setProposalStructure] = useState<ProposalHeading[]>([]);
  const isStructureInitialMount = useRef(true);
  const lastServerStructureRef = useRef<string>('');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Sync proposal structure from tender data
  useEffect(() => {
    const serverData = tender?.customProposalStructure;
    const targetData = serverData && serverData.length > 0 ? serverData : [];
    const targetDataStr = JSON.stringify(targetData);

    if (targetDataStr === lastServerStructureRef.current) {
      return;
    }

    lastServerStructureRef.current = targetDataStr;

    setProposalStructure((prev) => {
      if (JSON.stringify(prev) !== targetDataStr) {
        return targetData;
      }
      return prev;
    });
  }, [tender?.customProposalStructure]);

  // Auto-save proposal structure changes
  useEffect(() => {
    if (isStructureInitialMount.current) {
      isStructureInitialMount.current = false;
      return;
    }
    if (!account?.clientId || !account?.uid || !tender?.id) {
      return;
    }

    const serverStructure = tender?.customProposalStructure;
    const normalizedServer = serverStructure && serverStructure.length > 0 ? serverStructure : [];
    const normalizedLocal =
      proposalStructure && proposalStructure.length > 0 ? proposalStructure : [];
    const isDifferent = JSON.stringify(normalizedLocal) !== JSON.stringify(normalizedServer);

    if (!isDifferent) {
      return;
    }

    const handler = setTimeout(async () => {
      try {
        if (!tender?.id) {
          return;
        }
        const tenderDocRef = doc(firestore, 'tenders', tender.id);
        const sanitizedStructure = JSON.parse(JSON.stringify(proposalStructure));
        await updateDoc(tenderDocRef, { customProposalStructure: sanitizedStructure });
        showSnackbar('Proposal structure saved!', 'success');
      } catch (_error) {
        showSnackbar('Failed to save proposal structure', 'error');
      }
    }, 1000);

    return () => clearTimeout(handler);
  }, [
    proposalStructure,
    account?.clientId,
    account?.uid,
    tender?.id,
    showSnackbar,
    tender?.customProposalStructure,
  ]);

  return {
    proposalStructure,
    setProposalStructure,
    sensors,
  };
};
